import { stringToArray, arrayToString, stringToObject, objectToString } from 'helpers/formatters/string_formats';

import { setUserBounds } from './actions/coordinates';

import {
  changeAddressFilterGroup,
  changeCustomFieldFilters,
  changeIndustrialFilterGroup,
  changeLeaseFilterGroup,
  changeLeasePage,
  changeLeaseSort,
  changePropertyFilterGroup,
  changeSaleFilterGroup,
  changeSalePage,
  changeSaleSort,
  changeSearchText,
  setAssetClasses,
  setAvailabilities,
  setBuildingClasses,
  setInvestmentTypes
} from './actions/query';

import { switchTab, switchViewToList, switchViewToMap } from './actions/ui';
import { getCurrentTab, getCurrentView } from './selectors/ui';

const queryParams = {
  tab: {
    action: value => value ? switchTab(value) : { type: null },
    selector: getCurrentTab
  },
  view: {
    action: (value) => {
      if (!value) return { type: null };
      return value === 'map' ? switchViewToMap() : switchViewToList();
    },
    selector: getCurrentView
  },
  buildingNum: {
    action: value => changeAddressFilterGroup({ buildingNumber: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.buildingNumber || ''
  },
  street: {
    action: value => changeAddressFilterGroup({ street: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.street || ''
  },
  cities: {
    action: value => changeAddressFilterGroup({ cities: value }),
    defaultValue: [],
    selector: state => state.query.addressFilter.cities || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  market: {
    action: value => changeAddressFilterGroup({ market: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.market || ''
  },
  subMarket: {
    action: value => changeAddressFilterGroup({ subMarket: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.subMarket || ''
  },
  microMarket: {
    action: value => changeAddressFilterGroup({ microMarket: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.microMarket || ''
  },
  state: {
    action: value => changeAddressFilterGroup({ state: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.state || ''
  },
  zip: {
    action: value => changeAddressFilterGroup({ zip: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.zip || ''
  },
  maxSalePrice: {
    action: value => changeSaleFilterGroup({ maxSalesPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxSalesPrice || ''
  },
  minSalePrice: {
    action: value => changeSaleFilterGroup({ minSalesPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minSalesPrice || ''
  },
  maxSalesDate: {
    action: value => changeSaleFilterGroup({ maxSalesDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxSalesDate || ''
  },
  minSalesDate: {
    action: value => changeSaleFilterGroup({ minSalesDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minSalesDate || ''
  },
  maxLeasePrice: {
    action: value => changeLeaseFilterGroup({ maxLeasePrice: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.maxLeasePrice || ''
  },
  minLeasePrice: {
    action: value => changeLeaseFilterGroup({ minLeasePrice: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.minLeasePrice || ''
  },
  maxLeaseDate: {
    action: value => changeLeaseFilterGroup({ maxLeaseDate: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.maxLeaseDate || ''
  },
  minLeaseDate: {
    action: value => changeLeaseFilterGroup({ minLeaseDate: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.minLeaseDate || ''
  },
  maxLeaseExecutionDate: {
    action: value => changeLeaseFilterGroup({ maxLeaseExecutionDate: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.maxLeaseExecutionDate || ''
  },
  minLeaseExecutionDate: {
    action: value => changeLeaseFilterGroup({ minLeaseExecutionDate: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.minLeaseExecutionDate || ''
  },
  maxLeaseExpirationDate: {
    action: value => changeLeaseFilterGroup({ maxLeaseExpirationDate: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.maxLeaseExpirationDate || ''
  },
  minLeaseExpirationDate: {
    action: value => changeLeaseFilterGroup({ minLeaseExpirationDate: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.minLeaseExpirationDate || ''
  },
  maxLeaseSize: {
    action: value => changeLeaseFilterGroup({ maxLeaseSize: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.maxLeaseSize || ''
  },
  minLeaseSize: {
    action: value => changeLeaseFilterGroup({ minLeaseSize: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.minLeaseSize || ''
  },
  maxSfPrice: {
    action: value => changeSaleFilterGroup({ maxSfPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxSfPrice || ''
  },
  minSfPrice: {
    action: value => changeSaleFilterGroup({ minSfPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minSfPrice || ''
  },
  maxUnitPrice: {
    action: value => changeLeaseFilterGroup({ maxUnitPrice: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.maxUnitPrice || ''
  },
  minUnitPrice: {
    action: value => changeLeaseFilterGroup({ minUnitPrice: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.minUnitPrice || ''
  },
  maxCapRate: {
    action: value => changeSaleFilterGroup({ maxCapRate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxCapRate || ''
  },
  minCapRate: {
    action: value => changeSaleFilterGroup({ minCapRate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minCapRate || ''
  },
  leaseSpaceType: {
    action: value => changeLeaseFilterGroup({ leaseSpaceType: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.leaseSpaceType || ''
  },
  leaseType: {
    action: value => changeLeaseFilterGroup({ leaseType: value }),
    defaultValue: '',
    selector: state => state.query.leaseFilter.leaseType || ''
  },
  apn: {
    action: value => changePropertyFilterGroup({ apnNumber: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.apnNumber || ''
  },
  lotUnits: {
    action: value => changePropertyFilterGroup({ lotUnits: value }),
    defaultValue: 'sf',
    selector: state => state.query.propertyFilter.lotUnits || ''
  },

  minLotSize: {
    action: value => changePropertyFilterGroup({ minLotSize: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minLotSize || ''
  },
  maxLotSize: {
    action: value => changePropertyFilterGroup({ maxLotSize: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxLotSize || ''
  },
  minAvailableSf: {
    action: value => changePropertyFilterGroup({ minAvailableSF: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minAvailableSF || ''
  },
  maxAvailableSf: {
    action: value => changePropertyFilterGroup({ maxAvailableSF: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxAvailableSF || ''
  },
  maxBuildingSize: {
    action: value => changePropertyFilterGroup({ maxBuildingSize: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxBuildingSize || ''
  },
  minBuildingSize: {
    action: value => changePropertyFilterGroup({ minBuildingSize: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minBuildingSize || ''
  },
  maxYearBuilt: {
    action: value => changePropertyFilterGroup({ maxYearBuilt: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxYearBuilt || ''
  },
  minYearBuilt: {
    action: value => changePropertyFilterGroup({ minYearBuilt: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minYearBuilt || ''
  },
  minNumUnits: {
    action: value => changePropertyFilterGroup({ minNumUnits: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minNumUnits || ''
  },
  maxNumUnits: {
    action: value => changePropertyFilterGroup({ maxNumUnits: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxNumUnits || ''
  },
  minCeilingHeight: {
    action: value => changePropertyFilterGroup({ minCeilingHeight: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minCeilingHeight || ''
  },
  maxCeilingHeight: {
    action: value => changePropertyFilterGroup({ maxCeilingHeight: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxCeilingHeight || ''
  },
  availableRailProvider: {
    action: value => changeIndustrialFilterGroup({ availableRailProvider: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.availableRailProvider || ''
  },
  driveThroughAccess: {
    action: value => changeIndustrialFilterGroup({ driveThroughAccess: value }),
    defaultValue: false,
    selector: state => state.query.industrialFilter.driveThroughAccess || false
  },
  ready: {
    action: value => changeIndustrialFilterGroup({ ready: value }),
    defaultValue: false,
    selector: state => state.query.industrialFilter.ready || false
  },
  served: {
    action: value => changeIndustrialFilterGroup({ served: value }),
    defaultValue: false,
    selector: state => state.query.industrialFilter.served || false
  },
  maxJibCraneSpan: {
    action: value => changeIndustrialFilterGroup({ maxJibCraneSpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxJibCraneSpan || '',
    stringToValue: value => Number(value)
  },
  minJibCraneSpan: {
    action: value => changeIndustrialFilterGroup({ minJibCraneSpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minJibCraneSpan || '',
    stringToValue: value => Number(value)
  },
  lotBorderType: {
    action: value => changeIndustrialFilterGroup({ lotBorderType: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.lotBorderType || ''
  },
  lotStabilization: {
    action: value => changeIndustrialFilterGroup({ lotStabilization: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.lotStabilization || ''
  },
  maxAcresStabilized: {
    action: value => changeIndustrialFilterGroup({ maxAcresStabilized: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxAcresStabilized || '',
    stringToValue: value => Number(value)
  },
  maxBridgeCraneSpan: {
    action: value => changeIndustrialFilterGroup({ maxBridgeCraneSpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxBridgeCraneSpan || '',
    stringToValue: value => Number(value)
  },
  maxNumBridgeCranes: {
    action: value => changeIndustrialFilterGroup({ maxNumBridgeCranes: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxNumBridgeCranes || '',
    stringToValue: value => Number(value)
  },
  maxNumJibCranes: {
    action: value => changeIndustrialFilterGroup({ maxNumJibCranes: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxNumJibCranes || '',
    stringToValue: value => Number(value)
  },
  maxReadyCapacity: {
    action: value => changeIndustrialFilterGroup({ maxReadyCapacity: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxReadyCapacity || '',
    stringToValue: value => Number(value)
  },
  maxReadySpan: {
    action: value => changeIndustrialFilterGroup({ maxReadySpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxReadySpan || '',
    stringToValue: value => Number(value)
  },
  minAcresStabilized: {
    action: value => changeIndustrialFilterGroup({ minAcresStabilized: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minAcresStabilized || '',
    stringToValue: value => Number(value)
  },
  minBridgeCraneSpan: {
    action: value => changeIndustrialFilterGroup({ minBridgeCraneSpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minBridgeCraneSpan || '',
    stringToValue: value => Number(value)
  },
  minNumBridgeCranes: {
    action: value => changeIndustrialFilterGroup({ minNumBridgeCranes: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minNumBridgeCranes || '',
    stringToValue: value => Number(value)
  },
  minNumJibCranes: {
    action: value => changeIndustrialFilterGroup({ minNumJibCranes: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minNumJibCranes || '',
    stringToValue: value => Number(value)
  },
  minReadyCapacity: {
    action: value => changeIndustrialFilterGroup({ minReadyCapacity: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minReadyCapacity || '',
    stringToValue: value => Number(value)
  },
  minReadySpan: {
    action: value => changeIndustrialFilterGroup({ minReadySpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minReadySpan || '',
    stringToValue: value => Number(value)
  },
  servedRailProvider: {
    action: value => changeIndustrialFilterGroup({ servedRailProvider: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.servedRailProvider || ''
  },
  availabilities: {
    action: setAvailabilities,
    defaultValue: [],
    selector: state => state.query.availabilities || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  assetClasses: {
    action: setAssetClasses,
    defaultValue: [],
    selector: state => state.query.assetClasses || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  buildingClasses: {
    action: setBuildingClasses,
    defaultValue: [],
    selector: state => state.query.buildingClasses || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  customFields: {
    action: changeCustomFieldFilters,
    defaultValue: {},
    selector: state => state.query.customFieldFilters || {},
    stringToValue: stringToObject,
    valueToString: objectToString,
  },
  investmentTypes: {
    action: setInvestmentTypes,
    defaultValue: [],
    selector: state => state.query.investmentTypes || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  search_text: {
    action: changeSearchText,
    defaultValue: '',
    selector: state => state.query.searchText || ''
  },
  leasePage: {
    action: changeLeasePage,
    defaultValue: 1,
    selector: state => state.query.leasePage,
    stringToValue: value => Number(value) || 1
  },
  leaseSort: {
    action: value => changeLeaseSort(value || null),
    defaultValue: null,
    selector: state => state.query.leaseSort || null
  },
  salePage: {
    action: changeSalePage,
    defaultValue: 1,
    selector: state => state.query.salePage,
    stringToValue: value => Number(value) || 1
  },
  saleSort: {
    action: value => changeSaleSort(value || null),
    defaultValue: null,
    selector: state => state.query.saleSort || null
  },
  user_bounds: {
    action: setUserBounds,
    defaultValue: {},
    selector: state => state.coordinates.userBounds || {},
    stringToValue: stringToObject,
    valueToString: objectToString
  }
};

export default queryParams;
